// CPIP bootstrap (must be first): cpip-build-guard reloads the page when a different
// build already ran in this tab (P81-148827); cpip-share-init seeds the webpack share
// scope with window.React/ReactDOM (no-op in standalone); registerCommonHooks wires host
// hooks before any MFE mounts — covers both CPIP and standalone entry paths.
import "./cpip-build-guard";
import "./cpip-share-init";
import "./hooks/registerCommonHooks";
import "./tools/analytics/registerAnalytics";

import "@p81-common/ui/ui.umd.css";
import "@p81-common/ui/v2/variables.css";
import "safervpn-storybook/src/theme/fonts.css";
import "data/icons";
import { Provider } from "react-redux";
import { BrowserRouter } from "react-router-dom";
import { StylesProvider, createGenerateClassName } from "@mui/styles";
import store from "store/store";
import api from "tools/apiConfigurator";
import localStorage from "tools/localStorage";
import { initGTM } from "tools/analytics";
import { getUserConfirmation } from "tools/getUserConfirmation";
import Authorization from "routes/Authorization";
import { palette } from "styles/theme/palette";
import {
  ThemeProvider,
  Notification,
  RightSideBarProvider,
  Loading,
  CssBaseline,
} from "@p81-common/ui";
import { injectEnvsScript, env } from "@p81-common/shell-core-js";
import { useEffect } from "react";
import { useToggle } from "react-use";
import { isCheckpointDomain } from "@p81-common/shell-core-js";
import type { AppProps } from "types";
import { URLTrackerProvider } from "data/URLTrackerContext";
import { initVersionManager } from "tools/versionManager";
import { restoreStyles, trackStyles, stopTracking } from "./cpipStylePersistence";

// Fix for JSS class name collision between host and MFE.
// Without this, both generate incremental class names (.jss1, .jss2...) that can
// collide when Module Federation load order varies (race condition), causing
// wrong styles to be applied (e.g., cyan backgrounds on form labels).
// Using a fixed seed ensures deterministic, consistent class name generation.
const generateClassName = createGenerateClassName({
  seed: "p81",
});

function App(props?: AppProps) {
  const [willMount, toggleWillMount] = useToggle(true);

  useEffect(() => {
    // Check for version changes and handle cache invalidation
    // This must run before other initialization to prevent loading old cached code
    if (isCheckpointDomain) {
      restoreStyles();
      trackStyles();
      initVersionManager();
    }

    injectEnvsScript().then(() => {
      localStorage.init();
      api.init();
      initGTM();
      toggleWillMount(false);
    });
    return () => {
      if (isCheckpointDomain) {
        // Styles clean up when changing to other apps in the Infinity Portal
        stopTracking();
        document
          .querySelectorAll("[data-meta=makeStyles]")
          .forEach((el) => el.remove());
        document
          .querySelectorAll("[name=p81styles]")
          .forEach((el) => el.remove());

        // Third party scripts cleanup
        document.querySelector(".intercom-lightweight-app")?.remove();
        document
          .querySelectorAll('[title*="Perimeter 81 Status"]')
          .forEach((el) => el.remove());
        // Keep <portal>: singleton, not re-added on CPIP re-entry (P81-131415)
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <StylesProvider generateClassName={generateClassName}>
      <ThemeProvider palette={palette}>
        <CssBaseline />
        {willMount ? (
          <Loading />
        ) : (
          <Provider store={store}>
            <BrowserRouter
              basename={isCheckpointDomain ? env.REACT_APP_CP_BASENAME : ""}
              getUserConfirmation={getUserConfirmation}
            >
              <RightSideBarProvider>
                <URLTrackerProvider>
                  <Authorization {...props} />
                </URLTrackerProvider>
                <Notification />
              </RightSideBarProvider>
            </BrowserRouter>
          </Provider>
        )}
      </ThemeProvider>
    </StylesProvider>
  );
}

export default App;
